/**
 * Define a reducer and the default state for this reducer here 'reducer.ts'
 */
/** *** */
import { produce } from "immer";
import _ from "lodash";
import { RecursivePartial } from "src/util/types";
import firebase from "firebase/app";
import Bugsnag from "@bugsnag/js";
import type {
  tErrorAction,
  tErrorData,
  tErrorHandlerAction,
  tErrorHandlerState,
  tErrorInfoAction,
  tErrorLogAction,
  tErrorWarningAction,
  tPermanentRibbon,
  tRibbon,
} from "./types";

const kDefaultErrorHandlerState: tErrorHandlerState = {
  errors: {},
  showRibbon: false,
  permanent: false,
  permanentErrorType: "SUCCESS",
  permanentMessage: "",
  message: "",
  errorType: "SUCCESS",
};

type tpAddError = (state: tErrorHandlerState, myError: tErrorData) => tErrorHandlerState;
const addError: tpAddError = (state, myError) => {
  const aTimestamp = new Date().toISOString();

  return _.merge<tErrorHandlerState, RecursivePartial<tErrorHandlerState>>(state, {
    errors: { [aTimestamp]: myError },
  });
};

type tpHandleError = (state: tErrorHandlerState, action: tErrorAction) => tErrorHandlerState;
const handleError: tpHandleError = (state, action) => {
  const aErrorMessage = action.error.stack;

  console.error(aErrorMessage);
  Bugsnag.notify(action.error);

  return addError(state, {
    severity: "error",
    error: action.error,
  });
};

type tpHandleErrorWarning = (state: tErrorHandlerState, action: tErrorWarningAction) => tErrorHandlerState;
const handleErrorWarning: tpHandleErrorWarning = (state, action) => {
  const aErrorMessage = action.error.stack;

  console.warn(aErrorMessage);

  return addError(state, {
    severity: "warning",
    error: action.error,
  });
};

type tpHandleErrorInfo = (state: tErrorHandlerState, action: tErrorInfoAction) => tErrorHandlerState;
const handleErrorInfo: tpHandleErrorInfo = (state, action) => {
  const aErrorMessage = action.error.stack;

  console.info(aErrorMessage);

  return addError(state, {
    severity: "error",
    error: action.error,
  });
};

type tpHandleErrorLog = (state: tErrorHandlerState, action: tErrorLogAction) => tErrorHandlerState;
const handleErrorLog: tpHandleErrorLog = (state, action) => {
  return addError(state, {
    severity: "warning",
    error: action.error,
  });
};

type tpHandleClearErrors = (state: tErrorHandlerState) => tErrorHandlerState;
const handleClearErrors: tpHandleClearErrors = (state) => {
  state.errors = kDefaultErrorHandlerState.errors;
  return state;
};

type tpPermanentBanner = (state: tErrorHandlerState, action: tPermanentRibbon) => tErrorHandlerState;
const handlePermanentBannerDisplay: tpPermanentBanner = (state, action) => {
  state.permanent = true;

  if (action.message === undefined) {
    state.permanent = false;
  } else if (typeof action.message === "string") {
    state.permanentErrorType = "SUCCESS";
    state.permanentMessage = action.message;
  } else {
    state.permanentErrorType = "ERROR";
    state.permanentMessage = action.message.message;
  }
  return state;
};

type tpDisplayRibbon = (state: tErrorHandlerState, action: tRibbon) => tErrorHandlerState;
const handleDisplayRibbon: tpDisplayRibbon = (state, action) => {
  state.showRibbon = true;

  if (action.message === undefined) {
    state.showRibbon = false;
  } else if (typeof action.message === "string") {
    state.errorType = "SUCCESS";
    state.message = action.message;
    firebase.analytics().logEvent(
      `Success: ${state.message}\n`,
      // + `${s.UserReducer.profile.firstName} ${s.UserReducer.profile.lastName} of ${s.UserReducer.profile.companyRefPath}`,
    );
  } else {
    state.errorType = "ERROR";
    state.message = action.message.message;
    firebase.analytics().logEvent(
      `Error: ${state.message}\n`,
      // + `${s.UserReducer.profile.firstName} ${s.UserReducer.profile.lastName} of ${ s.UserReducer.profile.companyRefPath } had an error: ${JSON.stringify(state.message)}`,
    );
    Bugsnag.notify(action.message);

    return addError(state, {
      severity: "error",
      error: action.message,
    });
  }

  return state;
};

export const ErrorHandlerReducer = produce(
  (state: tErrorHandlerState = kDefaultErrorHandlerState, action: tErrorHandlerAction): tErrorHandlerState => {
    switch (action.type) {
      case "ERROR":
        return handleError(state, action);
      case "ERROR_WARNING":
        return handleErrorWarning(state, action);
      case "ERROR_INFO":
        return handleErrorInfo(state, action);
      case "ERROR_LOG":
        return handleErrorLog(state, action);
      case "CLEAR_ERRORS":
        return handleClearErrors(state);
      case "RIBBON":
        return handleDisplayRibbon(state, action);
      case "PERMANENT_RIBBON":
        return handlePermanentBannerDisplay(state, action);
      default:
        return state;
    }
  },
);
